import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authApi, sessionStorage, type AuthUser } from '../services/api'

interface AuthContextType {
  recruiter: AuthUser | null
  isLoading: boolean
  login: (username: string, password: string) => Promise<AuthUser>
  logout: () => void
  updateRecruiter: (recruiter: AuthUser) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [recruiter, setRecruiter] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const updateRecruiter = (next: AuthUser) => {
    setRecruiter(next)
    sessionStorage.setRecruiter(next)
    sessionStorage.setRecruiterUuid(next.uuid)
  }

  useEffect(() => {
    const token = sessionStorage.getToken()
    const storedRecruiter = sessionStorage.getRecruiter()

    if (token && storedRecruiter) {
      authApi.getCurrentUser()
        .then((response) => {
          if (response.status === 'success' && response.recruiter) {
            updateRecruiter(response.recruiter)
          } else {
            sessionStorage.clearAuth()
          }
        })
        .catch(() => {
          sessionStorage.clearAuth()
        })
        .finally(() => {
          setIsLoading(false)
        })
    } else {
      setIsLoading(false)
    }
  }, [])

  const login = async (username: string, password: string) => {
    try {
      const response = await authApi.login(username, password)
      if (response.status === 'success' && response.recruiter && response.token) {
        sessionStorage.clearFormDraft()
        sessionStorage.setToken(response.token)
        updateRecruiter(response.recruiter)
        return response.recruiter
      }
      throw new Error('Login failed')
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Login failed'
      throw new Error(message)
    }
  }

  const logout = () => {
    setRecruiter(null)
    sessionStorage.clearAuth()
  }

  return (
    <AuthContext.Provider
      value={{
        recruiter,
        isLoading,
        login,
        logout,
        updateRecruiter,
        isAuthenticated: !!recruiter,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    console.error('useAuth was called outside AuthProvider; falling back to unauthenticated state')
    return {
      recruiter: null,
      isLoading: false,
      login: async () => {
        throw new Error('AuthProvider is not mounted')
      },
      logout: () => {},
      updateRecruiter: () => {},
      isAuthenticated: false,
    }
  }
  return context
}
